import {checkIfSpecialBuilding} from "../../buildings/checkIfSpecialBuilding";
import {
  calculateAvailableConstructionCellsForSpecialBuilding
} from "./calculateAvailableConstructionCellsForSpecialBuilding";
import {
  calculateAvailableConstructionCellsForCommonBuilding,
  createCommonBuildingCellAvailabilityCheck
} from "./calculateAvailableConstructionCellsForCommonBuilding";
import {
  selectCurrentPlayer,
  selectHexes,
  selectPendingBuildings,
  selectSpecialBuildings
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calculateBlockedConstructionCells} from "./calculateBlockedConstructionCells";
import {selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {MINE_EXPLORATION} from "../../../configs/factions/dwarves";
import {
  calculateAvailableConstructionCellsForMineExploration
} from "../../factions/dwarves/calculateAvailableConstructionCellsForMineExploration";
import {calculateFutureCitiesCache} from "../calculateFutureCitiesCache";
import {calculateQuarterZoneCells} from "./calculateQuarterZoneCells";
import {
  calculateBlockedCellsForFutureQuarterPotential,
  FUTURE_QUARTER_POTENTIAL_ERROR_MESSAGES,
  TOKENS_REQUIRING_FUTURE_QUARTER_POTENTIAL_CHECK
} from "./calculateBlockedCellsForFutureQuarterPotential";

/**
 * Ячейки строительства выбранного строения: доступные, заблокированные и
 * причина блокировки каждой.
 *
 * targetHexKey — нужна только одна ячейка (клик, план): доступные и
 * заблокированные считаются лишь для нее, и результат по ней тот же, что у
 * полного расчета. Это верно, пока каждое правило блокировки отбирает
 * подмножество доступных ячеек и решает по каждой ячейке независимо от прочих
 * доступных — так устроены все правила ниже (проверяется тестом на совпадение
 * с полным расчетом). Дорого в полном расчете именно это: поиск пути
 * строительства от каждой ячейки поля.
 *
 * sharedCache — объект для серии вызовов по состояниям, которые различаются
 * ТОЛЬКО выбранным строением (бот перебирает все строения на одном поле): то,
 * что от выбранного строения не зависит, — кэш городов с учетом отложенных
 * строений, доступные ячейки обычных строений и зона кварталов, —
 * считается один раз на серию. Результаты из кэша только читаются, поэтому
 * отдавать их нескольким вызовам безопасно
 */
export const calculateConstructionCells = (state, {targetHexKey = null, sharedCache = null} = {}) => {
  const selectedToken = selectSelectedToken(state);
  const hexes = selectHexes(state);
  const currentPlayer = selectCurrentPlayer(state);
  const pendingBuildings = selectPendingBuildings(state);
  const specialBuildings = selectSpecialBuildings(state);

  const getShared = (key, calculate) => {
    if (!sharedCache) {
      return calculate();
    }
    if (!Object.prototype.hasOwnProperty.call(sharedCache, key)) {
      sharedCache[key] = calculate();
    }
    return sharedCache[key];
  };

  // Кэш городов нужен и расчетам ниже — считается один раз и передается им
  const futureCitiesCache = getShared(
    'futureCitiesCache', () => calculateFutureCitiesCache(hexes, specialBuildings, pendingBuildings)
  );

  const isSpecialBuildingSelected = checkIfSpecialBuilding(selectedToken);
  const isTargeted = targetHexKey !== null;

  let availableCells;
  if (isPlayerFaction(currentPlayer, FACTIONS.DWARVES) && selectedToken === MINE_EXPLORATION) {
    availableCells = calculateAvailableConstructionCellsForMineExploration(state);
  } else if (isSpecialBuildingSelected) {
    availableCells = calculateAvailableConstructionCellsForSpecialBuilding(state, futureCitiesCache);
  } else if (isTargeted) {
    availableCells = createCommonBuildingCellAvailabilityCheck(state)(targetHexKey) ? [targetHexKey] : [];
  } else {
    availableCells = getShared('commonBuildingCells', () => calculateAvailableConstructionCellsForCommonBuilding(state));
  }
  if (isTargeted) {
    availableCells = availableCells.includes(targetHexKey) ? [targetHexKey] : [];
  }

  const {blockedCells, blockedCellsErrorMessage} = calculateBlockedConstructionCells(
    selectedToken, hexes, futureCitiesCache, currentPlayer.color, availableCells
  );

  // Сообщение при клике — теперь ПОЯЧЕЕЧНОЕ (Map), а не одна строка на весь
  // selectedToken: у одной и той же ячейки может быть только ОДНА исходная
  // причина блокировки (см. ниже), и текст должен соответствовать именно ей,
  // а не показывать составное сообщение "либо то, либо это"
  const blockedCellsErrorMessages = new Map();
  blockedCells.forEach((cellKey) => blockedCellsErrorMessages.set(cellKey, blockedCellsErrorMessage));

  // Ферма/казарма/базовый квартал, рядом с которыми в будущем невозможно
  // заложить ни один квартал (тупиковая застройка — город не сможет
  // расшириться через эту ячейку), остаются в числе доступных, но
  // подсвечиваются красным как заблокированные — та же зона влияния
  // кварталов, что подсвечивает игроку оверлей "Зона кварталов" (VisualToggles),
  // см. calculateBlockedCellsForFutureQuarterPotential.
  let finalBlockedCells = blockedCells;
  if (TOKENS_REQUIRING_FUTURE_QUARTER_POTENTIAL_CHECK.includes(selectedToken)) {
    const quarterZoneCells = getShared('quarterZoneCells', () => calculateQuarterZoneCells(state, futureCitiesCache));
    const futureQuarterBlockedCells = calculateBlockedCellsForFutureQuarterPotential(
      selectedToken, availableCells, hexes, futureCitiesCache, currentPlayer.color, quarterZoneCells
    );
    if (futureQuarterBlockedCells.size > 0) {
      finalBlockedCells = new Set([...blockedCells, ...futureQuarterBlockedCells]);
      futureQuarterBlockedCells.forEach((cellKey) => {
        // Приоритет у исходной причины (ближе 2 ячеек к кварталу другого
        // города и т.п.) — она более фундаментальна и уже определена для
        // этой ячейки; сообщение о потенциале будущего квартала — только
        // если ячейка НЕ заблокирована исходным правилом
        if (!blockedCellsErrorMessages.has(cellKey)) {
          blockedCellsErrorMessages.set(cellKey, FUTURE_QUARTER_POTENTIAL_ERROR_MESSAGES[selectedToken]);
        }
      });
    }
  }

  return {
    availableCells,
    isSpecialBuildingSelected,
    blockedCells: finalBlockedCells,
    blockedCellsErrorMessages
  };
};
